import { Controller } from "@hotwired/stimulus"
import { formatTime } from "../lib/time"

export default class extends Controller {
    static targets = [
        "description",
        "audioButton",
        "iconPlay",
        "iconPause",
        "iconLoading",
        "currentTime",
        "duration",
        "progressBar",
        "progressBarFill",
        "volumeContainer",
        "volumeBar",
        "volumeBarFill",
        "volumeIcon",
        "mutedVolumeIcon",
        "trackTitle",
        "trackAuthor"
    ]

    static values = {
        url: String,
        seekTime: { type: Number, default: 10 }
    }

    connect() {
        console.log("[tts_controller] connected 2")
        this.audio = null
        this.isPlaying = false
        this.isPaused = false
        this.audioLoaded = false

        this.boundDescriptionReady = this._handleDescriptionReady.bind(this)
        document.addEventListener("poi:description-ready", this.boundDescriptionReady)
        this.boundDescriptionFrameLoad = this._handleDescriptionFrameLoad.bind(this)
        document.addEventListener("turbo:frame-load", this.boundDescriptionFrameLoad)
        this.boundAudioPreparation = this._handleAudioPreparation.bind(this)
        document.addEventListener("poi:audio-preparation", this.boundAudioPreparation)

        // Bound event handlers for cleanup
        this.boundSetInitialTime = this._setInitialTime.bind(this)
        this.boundHandleTimeUpdate = this._handleTimeUpdate.bind(this)
        this.boundHandleEnded = this._handleEnded.bind(this)
        this.boundHandleCanPlay = this._handleCanPlay.bind(this)

        this._updateUI()

        if (this.element.dataset.descriptionAvailable === "true") {
            this._enableAudioPlayer()
            this._announceDescriptionReady()
        } else if (this.hasAudioButtonTarget) {
            const descriptionFrame = document.querySelector(
                `turbo-frame#description[data-poi-id="${this.element.dataset.poiId}"][data-description-available="true"]`
            )
            this._setDescriptionReady(
                this.element.dataset.descriptionReady === "true" || Boolean(descriptionFrame)
            )
        }
    }

    disconnect() {
        document.removeEventListener("poi:description-ready", this.boundDescriptionReady)
        document.removeEventListener("turbo:frame-load", this.boundDescriptionFrameLoad)
        document.removeEventListener("poi:audio-preparation", this.boundAudioPreparation)
        if (this.audio) {
            this.audio.removeEventListener("loadedmetadata", this.boundSetInitialTime)
            this.audio.removeEventListener("timeupdate", this.boundHandleTimeUpdate)
            this.audio.removeEventListener("ended", this.boundHandleEnded)
            this.audio.removeEventListener("canplaythrough", this.boundHandleCanPlay)
            this.audio.pause()
            this.audio = null
        }
    }

    _announceDescriptionReady() {
        this._enableAudioPlayer()
        document.dispatchEvent(new CustomEvent("poi:description-ready", {
            detail: { poiId: this.element.dataset.poiId }
        }))
    }

    _enableAudioPlayer() {
        const player = document.querySelector(
            `#audio-player[data-poi-id="${this.element.dataset.poiId}"]`
        )
        const button = player?.querySelector('[data-tts-target="audioButton"]')
        if (!button) return

        button.disabled = false
        button.classList.remove("opacity-50", "cursor-not-allowed")
    }

    _handleDescriptionReady(event) {
        if (!this.hasAudioButtonTarget || event.detail.poiId !== this.element.dataset.poiId) return
        this._setDescriptionReady(true)
    }

    _handleDescriptionFrameLoad(event) {
        if (!this.hasAudioButtonTarget) return

        const frame = event.target
        if (frame?.id !== "description" || frame.dataset.poiId !== this.element.dataset.poiId) return
        if (frame.dataset.descriptionAvailable === "true") this._setDescriptionReady(true)
    }

    async _handleAudioPreparation(event) {
        if (event.detail.poiId !== this.element.dataset.poiId) return
        if (!event.detail.loading && event.detail.ready && event.detail.audioBlob) {
            this._loadPreparedAudio(event.detail.audioBlob)
        }
        this._setLoading(event.detail.loading)
        if (!event.detail.loading && !event.detail.ready) {
            this.audioButtonTarget.disabled = true
            this._hide(this.iconPlayTarget)
        }
    }

    _loadPreparedAudio(audioBlob) {
        if (this.audio) URL.revokeObjectURL(this.audio.src)

        this.audio = new Audio(URL.createObjectURL(audioBlob))
        this._setupAudioEventListeners()
        this._setInitialVolume()
        this.audioLoaded = true
        this.isPlaying = false
        this.isPaused = true
        this._updateUI()
    }

    _setDescriptionReady(ready) {
        this.audioButtonTarget.disabled = !ready
        this.audioButtonTarget.classList.toggle("opacity-50", !ready)
        this.audioButtonTarget.classList.toggle("cursor-not-allowed", !ready)
    }

    async togglePlay(event) {
        console.log("[tts_controller] togglePlay")
        if (event) event.preventDefault()
        if (this._isFormFieldFocused()) return

        const poiId = this.element.dataset.poiId
        if (!poiId) {
            console.error("POI ID manquant.")
            return
        }

        // If audio is loaded and playing, pause it
        if (this.audio && this.isPlaying && !this.isPaused) {
            this.audio.pause()
            this.isPaused = true
            this.isPlaying = false
            this._updateUI()
            return
        }

        // If audio is loaded and paused, resume it
        if (this.audio && this.isPaused) {
            this.audio.play()
            this.isPaused = false
            this.isPlaying = true
            this._updateUI()
            return
        }

        // Otherwise, fetch and play new audio from backend
        this._setLoading(true)
        try {
            const response = await fetch(`/pois/${poiId}/audio_file`)
            if (!response.ok) {
                throw new Error("Erreur lors de la génération de l'audio")
            }
            const blob = await response.blob()

            if (this.audio) {
                this.audio.pause()
                this.audio = null
            }

            this.audio = new Audio(URL.createObjectURL(blob))
            this._setupAudioEventListeners()
            this._setInitialVolume()

            this.audioLoaded = true
            this.isPlaying = true
            this.isPaused = false
            this._updateUI()
            this.audio.play()
        } catch (e) {
            console.error(e.message)
        } finally {
            this._setLoading(false)
        }
    }

    // Seek to position when clicking on progress bar
    seek(event) {
        if (!this.audio || !this.audioLoaded) return

        const { left, width } = this.progressBarTarget.getBoundingClientRect()
        const clickPosition = event.clientX - left
        const percentage = Math.max(0, Math.min(1, clickPosition / width))

        this.audio.currentTime = percentage * this.audio.duration
        this.progressBarFillTarget.style.width = `${percentage * 100}%`

        if (this.audio.paused && this.audioLoaded) {
            this.audio.play()
            this.isPlaying = true
            this.isPaused = false
            this._updateUI()
        }
    }

    // Go to start of track
    goToStart() {
        if (!this.audio || !this.audioLoaded) return
        this._goToTime(0)
    }

    // Go to end of track
    goToEnd() {
        if (!this.audio || !this.audioLoaded) return
        this._goToTime(this.audio.duration)
    }

    // Seek forward by seekTime seconds (keyboard: right arrow)
    seekForward(event) {
        if (this._isFormFieldFocused()) return
        if (!this.audio || !this.audioLoaded) return

        const seekValue = this.seekTimeValue
        this._goToTime(this.audio.currentTime + seekValue)
    }

    // Seek backward by seekTime seconds (keyboard: left arrow)
    seekBackward(event) {
        if (this._isFormFieldFocused()) return
        if (!this.audio || !this.audioLoaded) return

        const seekValue = this.seekTimeValue
        this._goToTime(this.audio.currentTime - seekValue)
    }

    // Toggle mute (keyboard: M key)
    toggleMute(event) {
        if (this._isFormFieldFocused()) return
        if (!this.audio) return

        this.audio.muted = !this.audio.muted
        this._updateVolumeIcon()
    }

    // Toggle volume bar visibility
    toggleVolumeBar() {
        if (!this.hasVolumeContainerTarget) return
        this.volumeContainerTarget.classList.toggle("w-0")
        this.volumeContainerTarget.classList.toggle("w-24")
        this.volumeContainerTarget.classList.toggle("opacity-0")
        this.volumeContainerTarget.classList.toggle("opacity-100")
    }

    // Set volume when clicking on volume bar
    setVolume(event) {
        if (!this.audio || !this.hasVolumeBarTarget) return

        const { left, width } = this.volumeBarTarget.getBoundingClientRect()
        const clickPosition = event.clientX - left
        const percentage = Math.max(0, Math.min(1, clickPosition / width))

        this.volumeBarFillTarget.style.width = `${percentage * 100}%`
        this.audio.volume = percentage

        // Update muted state based on volume
        if (percentage === 0) {
            this.audio.muted = true
        } else if (this.audio.muted) {
            this.audio.muted = false
        }
        this._updateVolumeIcon()
    }

    // Private methods
    _setupAudioEventListeners() {
        this.audio.addEventListener("loadedmetadata", this.boundSetInitialTime)
        this.audio.addEventListener("timeupdate", this.boundHandleTimeUpdate)
        this.audio.addEventListener("ended", this.boundHandleEnded)
        this.audio.addEventListener("canplaythrough", this.boundHandleCanPlay)
    }

    _setInitialTime() {
        if (this.hasDurationTarget) {
            this.durationTarget.textContent = formatTime(this.audio.duration)
        }
        if (this.hasCurrentTimeTarget) {
            this.currentTimeTarget.textContent = formatTime(this.audio.currentTime)
        }
    }

    _handleTimeUpdate() {
        if (this.hasCurrentTimeTarget) {
            this.currentTimeTarget.textContent = formatTime(this.audio.currentTime)
        }

        if (this.hasProgressBarFillTarget && this.audio.duration) {
            const fillPercentage = (this.audio.currentTime / this.audio.duration) * 100
            this.progressBarFillTarget.style.width = `${fillPercentage}%`
        }
    }

    _handleEnded() {
        this.isPlaying = false
        this.isPaused = false
        this._updateUI()
    }

    _handleCanPlay() {
        this._setInitialTime()
    }

    _goToTime(time) {
        if (!this.audio) return

        // Clamp time to valid range
        const clampedTime = Math.max(0, Math.min(time, this.audio.duration))
        this.audio.currentTime = clampedTime

        if (this.audio.paused && this.audioLoaded) {
            this.audio.play()
            this.isPlaying = true
            this.isPaused = false
            this._updateUI()
        }
    }

    _setInitialVolume() {
        if (!this.audio) return

        // Set default volume to 80%
        this.audio.volume = 0.8

        if (this.hasVolumeBarFillTarget) {
            this.volumeBarFillTarget.style.width = `${this.audio.volume * 100}%`
        }
    }

    _updateUI() {
        this._updatePlayButton()
        this._updateVolumeIcon()
    }

    _updatePlayButton() {
        if (this.hasIconPlayTarget) {
            if (this.isPlaying) {
                this._hide(this.iconPlayTarget)
            } else {
                this._show(this.iconPlayTarget)
            }
        }

        if (this.hasIconPauseTarget) {
            if (this.isPlaying) {
                this._show(this.iconPauseTarget)
            } else {
                this._hide(this.iconPauseTarget)
            }
        }

        if (this.hasAudioButtonTarget) {
            if (this.isPlaying) {
                this.audioButtonTarget.classList.remove("from-blue-600", "to-blue-800")
                this.audioButtonTarget.classList.add("from-orange-500", "to-orange-700")
            } else {
                this.audioButtonTarget.classList.remove("from-orange-500", "to-orange-700")
                this.audioButtonTarget.classList.add("from-blue-600", "to-blue-800")
            }
        }
    }

    _updateVolumeIcon() {
        if (!this.hasVolumeIconTarget || !this.hasMutedVolumeIconTarget) return

        if (this.audio && this.audio.muted) {
            this._hide(this.volumeIconTarget)
            this._show(this.mutedVolumeIconTarget)
        } else {
            this._show(this.volumeIconTarget)
            this._hide(this.mutedVolumeIconTarget)
        }
    }

    _setLoading(loading) {
        if (this.hasAudioButtonTarget) {
            this.audioButtonTarget.disabled = loading
            this.audioButtonTarget.classList.toggle("opacity-50", loading)
            this.audioButtonTarget.classList.toggle("cursor-not-allowed", loading)
        }

        if (this.hasIconLoadingTarget) {
            if (loading) {
                this._show(this.iconLoadingTarget)
                if (this.hasIconPlayTarget) this._hide(this.iconPlayTarget)
                if (this.hasIconPauseTarget) this._hide(this.iconPauseTarget)
            } else {
                this._hide(this.iconLoadingTarget)
                if (this.hasIconPlayTarget && !this.isPlaying) this._show(this.iconPlayTarget)
            }
        }
    }

    _isFormFieldFocused() {
        const activeElement = document.activeElement
        const formFields = ['INPUT', 'TEXTAREA', 'SELECT']

        return formFields.includes(activeElement.tagName) ||
               activeElement.hasAttribute('contenteditable') ||
               activeElement.closest('[contenteditable]') !== null
    }

    _show(el) {
        if (!el) return
        el.removeAttribute('hidden')
        el.classList.remove('!hidden', 'hidden')
        el.style.display = ''
        el.setAttribute('aria-hidden', 'false')
    }

    _hide(el) {
        if (!el) return
        el.setAttribute('hidden', '')
        el.classList.add('!hidden')
        el.style.display = 'none'
        el.setAttribute('aria-hidden', 'true')
    }
}
